import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Upload, TrendingUp, User } from 'lucide-react';
import FileManager from './components/FileManager';
import DataAnalysisWithChat from './components/DataAnalysisWithChat';
import LoginScreen from './components/LoginScreen';
import MyPage from './components/MyPage';
import DeveloperConsole from './components/DeveloperConsole';
import LoginActivityDashboard from './components/LoginActivityDashboard';
import UsageHistoryDashboard from './components/UsageHistoryDashboard';
import ContactSupport from './components/ContactSupport';
import ContactFaqManager from './components/ContactFaqManager';
import ContactSubjectManager from './components/ContactSubjectManager';
import ContactHistoryDashboard from './components/ContactHistoryDashboard';
import { apiClient, API_ENDPOINTS } from './config/api';
import './App.css';

interface AuthUser {
  id: number;
  username: string;
  role: 'admin' | 'user' | string;
  isActive?: boolean;
  createdAt?: string | null;
  lastLoginAt?: string | null;
}

interface AuthSession {
  expiresAt?: string | null;
  loginAt?: string | null;
}

interface LoginResult {
  errorMessage: string | null;
  requiresCaptcha?: boolean;
}

interface CaptchaConfig {
  captchaEnabled: boolean;
  captchaSiteKey: string;
}

function App() {
  const APP_VERSION = process.env.REACT_APP_VERSION || 'dev';
  const [authStatus, setAuthStatus] = useState<'loading' | 'authenticated' | 'unauthenticated'>('loading');
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const [currentSession, setCurrentSession] = useState<AuthSession | null>(null);
  const [currentView, setCurrentView] = useState<
    'files' | 'analysis' | 'mypage' | 'developer' | 'loginStatus' | 'usageHistory' |
    'contact' | 'contactFaq' | 'contactSubjects' | 'contactHistory'
  >('files');
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const logoutTimerRef = useRef<number | null>(null);
  const isAdmin = currentUser?.role === 'admin';
  const filesViewTitle = isAdmin ? '保存済みファイル' : '共有ファイルライブラリ';
  const filesViewDescription = isAdmin
    ? '分析対象のファイル一覧です。アップロードや名称変更、削除は管理者コンソールから行います。'
    : '管理者が公開したファイルを閲覧し、選択したデータを分析できます。';
  const filesNavLabel = isAdmin ? '保存済みファイル' : '共有ファイル';

  const fetchCurrentUser = useCallback(async () => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.authMe);
      setCurrentUser(response.data.user);
      setCurrentSession(response.data.session || null);
      setAuthStatus('authenticated');
    } catch (_error) {
      setCurrentUser(null);
      setCurrentSession(null);
      setAuthStatus('unauthenticated');
      setSelectedFileId(null);
      setCurrentView('files');
    }
  }, []);

  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  const handleLogin = useCallback(async (username: string, password: string, captchaToken: string) => {
    try {
      const response = await apiClient.post(API_ENDPOINTS.authLogin, { username, password, captchaToken });
      setCurrentUser(response.data.user);
      setCurrentSession(response.data.session || null);
      setAuthStatus('authenticated');
      setCurrentView('files');
      return { errorMessage: null } as LoginResult;
    } catch (error: any) {
      const errorCode = error?.response?.data?.error?.code;
      return {
        errorMessage: error?.response?.data?.error?.message || 'ログインに失敗しました。',
        requiresCaptcha: errorCode === 'captcha_required' || errorCode === 'captcha_failed',
      } as LoginResult;
    }
  }, []);

  const loadLoginProtectionConfig = useCallback(async () => {
    const response = await apiClient.get(API_ENDPOINTS.loginProtectionConfig);
    return response.data as CaptchaConfig;
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await apiClient.post(API_ENDPOINTS.authLogout);
    } catch (_error) {
      // Clear local auth state even if logout API fails.
    } finally {
      setCurrentUser(null);
      setCurrentSession(null);
      setAuthStatus('unauthenticated');
      setCurrentView('files');
      setSelectedFileId(null);
    }
  }, []);

  useEffect(() => {
    if (logoutTimerRef.current !== null) {
      window.clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    if (authStatus !== 'authenticated' || !currentSession?.expiresAt) {
      return;
    }

    const expiresAtMs = new Date(currentSession.expiresAt).getTime();
    if (Number.isNaN(expiresAtMs)) {
      return;
    }

    const delay = Math.max(expiresAtMs - Date.now(), 0);
    logoutTimerRef.current = window.setTimeout(() => {
      handleLogout();
    }, delay);

    return () => {
      if (logoutTimerRef.current !== null) {
        window.clearTimeout(logoutTimerRef.current);
        logoutTimerRef.current = null;
      }
    };
  }, [authStatus, currentSession, handleLogout]);

  const loadPasswordResetConfig = useCallback(async () => {
    const response = await apiClient.get(API_ENDPOINTS.passwordResetConfig);
    return response.data as { captchaEnabled: boolean; captchaSiteKey: string };
  }, []);

  const handlePasswordResetRequest = useCallback(async (email: string, captchaToken: string) => {
    try {
      const response = await apiClient.post(API_ENDPOINTS.passwordResetRequest, { email, captchaToken });
      return response.data?.message || null;
    } catch (error: any) {
      return error?.response?.data?.error?.message || 'パスワード再設定要求に失敗しました。';
    }
  }, []);

  const handlePasswordResetVerify = useCallback(async (token: string) => {
    try {
      await apiClient.get(API_ENDPOINTS.passwordResetVerify, { params: { token } });
      return null;
    } catch (error: any) {
      return error?.response?.data?.error?.message || '再設定URLの確認に失敗しました。';
    }
  }, []);

  const handlePasswordResetConfirm = useCallback(async (token: string, password: string, passwordConfirm: string) => {
    try {
      const response = await apiClient.post(API_ENDPOINTS.passwordResetConfirm, { token, password, passwordConfirm });
      return response.data?.message || null;
    } catch (error: any) {
      return error?.response?.data?.error?.message || 'パスワード変更に失敗しました。';
    }
  }, []);

  const handleFileSelect = useCallback((fileId: string | null) => {
    setSelectedFileId(fileId);
    if (fileId) {
      setCurrentView('analysis');
    }
  }, []);

  const renderContent = () => {
    switch (currentView) {
      case 'files':
        return (
          <FileManager
            onFileSelect={handleFileSelect}
            selectedFileId={selectedFileId}
            allowUpload={false}
            canManageUserSelection={isAdmin}
            canDeleteFiles={isAdmin}
            showOwnerUsername={isAdmin}
            title={filesViewTitle}
            description={filesViewDescription}
          />
        );
      case 'analysis':
        return <DataAnalysisWithChat fileId={selectedFileId} />;
      case 'contact':
        return <ContactSupport currentUser={currentUser} />;
      case 'mypage':
        return (
            <MyPage
              currentUser={currentUser}
              onLogout={handleLogout}
              onGoDeveloper={isAdmin ? () => setCurrentView('developer') : undefined}
              onGoContact={() => setCurrentView('contact')}
            />
          );
      case 'developer':
        if (!isAdmin) {
          return (
            <MyPage
              currentUser={currentUser}
              onLogout={handleLogout}
            />
          );
        }
        return (
          <DeveloperConsole
            selectedFileId={selectedFileId}
            onFileSelect={handleFileSelect}
            onGoLoginActivity={() => setCurrentView('loginStatus')}
            onGoUsageHistory={() => setCurrentView('usageHistory')}
            onGoContactFaq={() => setCurrentView('contactFaq')}
            onGoContactSubjects={() => setCurrentView('contactSubjects')}
            onGoContactHistory={() => setCurrentView('contactHistory')}
          />
        );
      case 'loginStatus':
        if (!isAdmin) {
          return (
            <MyPage
              currentUser={currentUser}
              onLogout={handleLogout}
            />
          );
        }
        return <LoginActivityDashboard onBackToDeveloper={() => setCurrentView('developer')} />;
      case 'usageHistory':
        if (!isAdmin) {
          return (
            <MyPage
              currentUser={currentUser}
              onLogout={handleLogout}
            />
          );
        }
        return <UsageHistoryDashboard onBackToDeveloper={() => setCurrentView('developer')} />;
      case 'contactFaq':
        if (!isAdmin) {
          return <MyPage currentUser={currentUser} onLogout={handleLogout} onGoContact={() => setCurrentView('contact')} />;
        }
        return <ContactFaqManager onBackToDeveloper={() => setCurrentView('developer')} />;
      case 'contactSubjects':
        if (!isAdmin) {
          return <MyPage currentUser={currentUser} onLogout={handleLogout} onGoContact={() => setCurrentView('contact')} />;
        }
        return <ContactSubjectManager onBackToDeveloper={() => setCurrentView('developer')} />;
      case 'contactHistory':
        if (!isAdmin) {
          return <MyPage currentUser={currentUser} onLogout={handleLogout} onGoContact={() => setCurrentView('contact')} />;
        }
        return <ContactHistoryDashboard onBackToDeveloper={() => setCurrentView('developer')} />;
      default:
        return (
          <FileManager
            onFileSelect={handleFileSelect}
            selectedFileId={selectedFileId}
            allowUpload={false}
            showOwnerUsername={isAdmin}
            title={filesViewTitle}
            description={filesViewDescription}
          />
        );
    }
  };

  if (authStatus === 'loading') {
    return (
      <div className="analysis-loading">
        <div className="loading-spinner"></div>
        <p>認証状態を確認中...</p>
      </div>
    );
  }

  if (authStatus !== 'authenticated') {
    return (
      <LoginScreen
        onLogin={handleLogin}
        onLoadLoginProtectionConfig={loadLoginProtectionConfig}
        onLoadPasswordResetConfig={loadPasswordResetConfig}
        onPasswordResetRequest={handlePasswordResetRequest}
        onPasswordResetVerify={handlePasswordResetVerify}
        onPasswordResetConfirm={handlePasswordResetConfirm}
      />
    );
  }

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-content">
          <h1 className="app-title">
            <TrendingUp className="title-icon" />
            <div className="app-title-text">
              <span className="service-name" onClick={() => setCurrentView('files')} style={{ cursor: 'pointer' }}>DataLink</span>
              <span className="service-subtitle">入札データ分析プラットフォーム</span>
            </div>
          </h1>
          <nav className="navigation">
            <button
              className={`nav-button ${currentView === 'files' ? 'active' : ''}`}
              onClick={() => setCurrentView('files')}
            >
              <Upload size={20} />
              {filesNavLabel}
            </button>
            <button
              className={`nav-button ${currentView === 'mypage' ? 'active' : ''}`}
              onClick={() => setCurrentView('mypage')}
            >
              <User size={20} />
              マイページ
            </button>
            <button
              className={`nav-button ${currentView === 'contact' ? 'active' : ''}`}
              onClick={() => setCurrentView('contact')}
            >
              お問い合わせ / ご要望
            </button>
          </nav>
        </div>
      </header>

      <main className={`main-content ${currentView === 'analysis' ? 'analysis-view' : ''}`}>
        {renderContent()}
      </main>

      <footer className="app-footer">
        <p>
          © {new Date().getFullYear()} 入札データ分析プラットフォーム - データ駆動型意思決定支援 -{' '}
          <a href="https://en-ds.jp/" target="_blank" rel="noopener noreferrer" className="footer-link">株式会社ｅｎ</a>
          <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', opacity: 0.85 }}>
            v{APP_VERSION} / {currentUser?.username}
          </span>
        </p>
      </footer>
    </div>
  );
}

export default App;
